import { ConnectEntity } from 'components/ConnectEntity';
import { ChooseReturn } from 'components/MappingForm/choose-return';
import { OrganisationDetails } from 'components/OrganisationDetails';
import { EditFriendlyName } from 'components/OrganisationDetails/edit-friendly-name';
import {
  extractColumnNamesFromFile,
  isValidExcelOrCsvFile,
} from 'components/OrganisationDetails/upload-transactions-drawer';
// Revert: uncomment to restore Upload transactions drawer
// import { UploadTransactionsDrawer } from 'components/OrganisationDetails/upload-transactions-drawer';
import { UploadTransactionsColumnsModal } from 'components/OrganisationDetails/upload-transactions-columns-modal';
import { VatOverviewPage, ReconciliationOverviewPage } from 'pages/vat';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import routes from 'routes';
import { AccountType, Provider } from 'store/api/enums';
import {
  clearMappingData,
  turnDrawerOff,
  turnDrawerOn,
  turnMessageOn,
  turnModalOn,
  updateMappingData,
} from 'store/app/actions';
import {
  DataLogStatus,
  DrawerType,
  LicenseType,
  UploadTransactionsSelection,
  WindowType,
} from 'store/app/enums';
import { MessageStates } from 'containers/MessageBox';
import { getMenuSize } from 'store/app/selectors';
import { DrawerData } from 'store/app/types';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { getBillingHasAdvancedRights } from 'store/billing/selectors';
import { getCommonCountries } from 'store/common/selectors';
import {
  downloadOrganisationAttachmentRequest,
  editOrganisationsRequest,
  fetchOrganisationsRequest,
  setUploadTransactionsTemp,
  updateOrganisationData,
} from 'store/organisation/actions';
import {
  EditOrganisationParams,
  Mapping,
  Organisation,
} from 'store/organisation/models';
import {
  getOrgLoading,
  getOrgMappings,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { fetchAddReturnDataRequest } from 'store/returns/actions';
import { MappingData } from 'store/returns/models';
import { getReturnData } from 'store/returns/selectors';
import { buildGroupReturnContextSearch } from 'utils/group-return-context';
import { returnMappingEditPath, reportViewPath } from 'utils/tifd-mapping';
import { activateViesRequest } from 'store/vat-checker/actions';
import { fetchVatLogsRequest } from 'store/vat/actions';
import {
  buildVatLogsFetchKey,
  hasInitialVatLogsBeenRequested,
  markInitialVatLogsRequested,
  shouldSkipVatLogsBurst,
} from 'utils/vatLogsFetchGuard';
import { getVatReturnLogs } from 'store/vat/selectors';
import { checkPageLoaded } from 'utils/check-page-loaded';
import { isPwaTaxReturnCreationAllowed } from 'utils/pwa-nav';
import {
  Preference,
  PreferenceActions,
  usePreferences,
  PreferenceSections,
} from 'utils/preferences';

export const PAGE_SIZE_ENTITY = 'page-size-entity';
export const PAGE_SIZE_EXCEL_TRANSACTIONS = 'page-size-excel-transactions';
export const HIDE_CANCELLED_ENTITY = 'hide-cancelled-entity';
export const excelColumnNames = [
  'Upload Date',
  'Transaction Date',
  'Due Date',
  'Source Type',
  'Transaction Number',
  'Reference',
  'Description',
  'Item',
  'Tax Name',
  'Tax Rate',
  'Currency',
  'Exchange Rate',
  'Account Name',
  'Name',
  'Quantity',
  'Unit Price',
  'Tax Inclusive',
  'Discount Amount',
  'VAT Paid',
  'Net Amount',
  'Total Amount',
  'Account Type',
  'Is Customer/Supplier',
  'VAT Number',
  'Country',
];

export const OrganisationPage: React.FC = () => {
  const parameters: any = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const preferences = usePreferences();
  const currentUser = useSelector(getAuthUser);
  const handlePreferences = (
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser)
      return preferences(
        action,
        PreferenceSections.ENTITIES,
        preference,
        currentUser?.userUuid
      );
    return action === PreferenceActions.GET ? undefined : false;
  };
  const selectedId = parameters.id;
  const organisations = useSelector(getOrgOrganisations);
  const mappings = useSelector(getOrgMappings);
  const loading = useSelector(getOrgLoading);
  const countries = useSelector(getCommonCountries);
  const returnData = useSelector(getReturnData);
  const vatReturnLogs = useSelector(getVatReturnLogs);
  const hasAdvancedUserRightsPre = useSelector(getBillingHasAdvancedRights);
  const userLicence = useSelector(getAuthUserLicence);

  const menuSize = useSelector(getMenuSize);
  const [selectedOrg, setSelectedOrg] = React.useState<Organisation>();
  const [selectedMaps, setSelectedMaps] = React.useState<Mapping[]>([]);
  const [hasAdvancedUserRights, setHasAdvancedUserRights] =
    React.useState<boolean>(false);
  const { t } = useTranslation();

  const downloadAttachment = (organisationId: string, attachmentId: string) => {
    dispatch(
      downloadOrganisationAttachmentRequest(organisationId, attachmentId)
    );
  };

  React.useEffect(() => {
    setHasAdvancedUserRights(
      hasAdvancedUserRightsPre || userLicence?.licenseType === LicenseType.TRIAL
    );
  }, [hasAdvancedUserRightsPre, userLicence]);

  React.useEffect(() => {
    if (!organisations) {
      dispatch(fetchOrganisationsRequest());
    }
    currentUser && checkPageLoaded(location.pathname, routes.organisations.all);
  }, []);

  React.useEffect(() => {
    if (organisations && selectedId) {
      const org = organisations.find((o) => o.uuid === selectedId);
      setSelectedOrg(org);
    }
  }, [organisations, selectedMaps, selectedId]);

  const vatLogsMapIdsKey = React.useMemo(() => {
    if (!selectedMaps?.length) return '';
    return [...new Set(selectedMaps.map((sm) => sm.mappingId))]
      .filter(Boolean)
      .sort()
      .join(',');
  }, [selectedMaps]);

  React.useEffect(() => {
    const connectionId = selectedOrg?.connectionId;
    if (!connectionId || !vatLogsMapIdsKey) return;
    const mapIds = vatLogsMapIdsKey.split(',');
    const fetchKey = buildVatLogsFetchKey(connectionId, mapIds);
    if (hasInitialVatLogsBeenRequested(fetchKey)) return;
    if (shouldSkipVatLogsBurst(fetchKey)) return;
    markInitialVatLogsRequested(fetchKey);
    dispatch(fetchVatLogsRequest(connectionId, mapIds));
  }, [dispatch, selectedOrg?.connectionId, vatLogsMapIdsKey]);

  const onAddEntity = () => {
    const drawerData: DrawerData = {
      title: t('organisation.add-new-entity'),
      subtitle: t('organisation.connect-entity-subtitle'),
      type: DrawerType.CONNECT_ENTITY,
      component: <ConnectEntity />,
    };
    dispatch(turnDrawerOn(drawerData));
  };

  React.useEffect(() => {
    if (mappings && selectedId) {
      const maps: Mapping[] = mappings.filter(
        (m) => m.organisationId === selectedId
      );
      setSelectedMaps(maps);
    }
  }, [mappings, selectedId]);

  const openEditFriendlyName = () => {
    if (selectedOrg) {
      const drawerData: DrawerData = {
        title: t('organisation.edit-details'),
        type: DrawerType.EDIT_FRIENDLY_NAME,
        wide: true,
        component: (
          <EditFriendlyName
            friendlyName={selectedOrg.friendlyName}
            organisationId={selectedOrg.uuid}
            onClose={() => dispatch(turnDrawerOff())}
            editOrganisationName={editOrganisationName}
            isAddMode={false}
          />
        ),
      };
      dispatch(turnDrawerOn(drawerData));
    } else alert(t('organisation.missing-connection-id'));
  };

  const editOrganisationName = (orgValues: EditOrganisationParams) => {
    if (!orgValues || !selectedId) return;
    const newDetails: EditOrganisationParams = {
      ...orgValues,
      clientCode: undefined,
      officeLocation: undefined,
      refId: selectedId,
    };
    dispatch(editOrganisationsRequest(newDetails));
    dispatch(turnDrawerOff());
  };

  const addReturn = (
    connectionId?: string,
    prefillData?: Pick<
      MappingData,
      'countryId' | 'typeId' | 'returnFrequency' | 'periodFromDate' | 'returnDueDate'
    >,
    context?: { fromGroup?: boolean; groupId?: string }
  ) => {
    if (!isPwaTaxReturnCreationAllowed()) {
      return;
    }
    const targetOrg =
      (connectionId
        ? organisations?.find((org) => org.connectionId === connectionId)
        : undefined) || selectedOrg;

    if (targetOrg) {
      const drawerData: DrawerData = {
        title: t('organisation.add-tax-return'),
        type: DrawerType.ADD_TAX_RETURN,
        component: (
          <ChooseReturn
            editMode={false}
            setSectionTouched={() => {}}
            groupContext={context}
          />
        ),
      };
      if (!returnData || returnData.connectionId !== targetOrg.connectionId) {
        dispatch(
          fetchAddReturnDataRequest(
            targetOrg.connectionId,
            undefined,
            prefillData?.typeId
          )
        );
      }
      dispatch(clearMappingData());
      if (prefillData) {
        dispatch(updateMappingData(prefillData as MappingData));
      }
      dispatch(turnDrawerOn(drawerData));
    } else alert(t('organisation.missing-connection-id'));
  };

  const editReturnMapping = (
    connectionId: string,
    mappingId: string,
    context?: { fromGroup?: boolean; groupId?: string }
  ) => {
    if (connectionId && mappingId) {
      const returnTypeId = mappings?.find(
        (mapping) => mapping.mappingId === mappingId
      )?.returnTypeId;
      const baseRoute = returnMappingEditPath(
        connectionId,
        mappingId,
        returnTypeId
      );
      const search = buildGroupReturnContextSearch(context);
      navigate(`${baseRoute}${search}`);
    } else alert(t('organisation.missing-connection-id'));
  };

  const openVatWindow = (
    connectionId: string,
    mappingId: string,
    context?: { fromGroup?: boolean; groupId?: string }
    //dataLogId?: string
  ) => {
    if (!isPwaTaxReturnCreationAllowed()) {
      return;
    }
    const modalData: DrawerData = {
      type: DrawerType.ADD_TAX_SUBMISSION,
      title: t('organisation.create-tax-submission'),
      component: (
        <VatOverviewPage
          connectionId={connectionId}
          mappingId={mappingId}
          groupContext={context}
        />
      ),
    };

    dispatch(turnDrawerOn(modalData));
  };

  const openReconciliationDrawer = () => {
    if (!selectedOrg) return;
    const selectedConnectionId = String(selectedOrg.connectionId ?? '').trim();
    const mappingIdSet = new Set((selectedMaps || []).map((m) => m.mappingId));
    const hasSubmittedSubmission = (vatReturnLogs || []).some((vrl: any) => {
      const rawStatus = vrl?.status;
      if (rawStatus == null) return false;
      const s = String(rawStatus).trim();
      const isSubmitted =
        s === DataLogStatus.SUBMITTED ||
        s.toLowerCase() === 'submitted' ||
        s.toUpperCase() === 'SUBMITTED' ||
        // Handle cases where backend sends enum key (e.g. "SUBMITTED") and callers use DataLogStatus[status]
        (DataLogStatus as any)[s] === DataLogStatus.SUBMITTED;

      if (!isSubmitted) return false;

      const conn =
        String(vrl?.connectionId ?? vrl?.applicationConnectionID ?? '').trim();
      if (!selectedConnectionId || conn !== selectedConnectionId) return false;

      const logMappingId = String(vrl?.mappingId ?? '').trim();
      return !logMappingId || mappingIdSet.size === 0
        ? true
        : mappingIdSet.has(logMappingId);
    });

    if (!hasSubmittedSubmission) {
      dispatch(
        turnMessageOn({
          title: t('organisation.reconciliation-unavailable-title'),
          description: t('organisation.reconciliation-unavailable-description'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }

    const drawerData: DrawerData = {
      type: DrawerType.RECONCILIATION,
      title: t('organisation.reconciliation-tab'),
      component: (
        <ReconciliationOverviewPage
          organisation={selectedOrg}
          selectedMaps={selectedMaps}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const vatGenerate = (
    connectionId: string,
    mappingId: string,
    dataLogId?: string,
    context?: { fromGroup?: boolean; groupId?: string }
  ) => {
    if (dataLogId) {
      const returnTypeId = mappings?.find(
        (mapping) => mapping.mappingId === mappingId
      )?.returnTypeId;
      navigate(reportViewPath(connectionId, mappingId, dataLogId, returnTypeId));
      return;
    }
    if (!isPwaTaxReturnCreationAllowed()) {
      return;
    }
    openVatWindow(connectionId, mappingId, context);
  };

  const checkVatReturn = () => {
    if (vatReturnLogs) {
      if (currentUser) {
        if (AccountType[currentUser.accountType] === AccountType.CLIENT) {
          return vatReturnLogs.filter(
            (vrl) =>
              DataLogStatus[vrl.status] ===
                DataLogStatus.AWAITING_CLIENT_APPROVAL ||
              DataLogStatus[vrl.status] === DataLogStatus.CANCELLED ||
              DataLogStatus[vrl.status] === DataLogStatus.CLIENT_APPROVED ||
              DataLogStatus[vrl.status] === DataLogStatus.LOCKED ||
              DataLogStatus[vrl.status] === DataLogStatus.SUBMITTED
          );
        }
      }
    }
    return vatReturnLogs;
  };

  const loadCustomersAndSuppliers = (connectionId: string) => {
    navigate(
      routes.vies.customersAndSuppliers.replace(':connectionId', connectionId)
    );
  };

  const activateVatChecker = (
    connectionId: string,
    entityName: string,
    uuid: string
  ) => {
    dispatch(activateViesRequest(connectionId, entityName));
    dispatch(updateOrganisationData(uuid, true));
    navigate(
      routes.vies.customersAndSuppliers.replace(':connectionId', connectionId)
    );
  };

  const checkTaxRatesAccess = () => {
    // Require explicit false: on hard refresh organisation data can hydrate before
    // tax-rate metadata runs, leaving taxEnabled undefined; !undefined wrongly blocks QBO users.
    if (
      selectedOrg &&
      selectedOrg.taxEnabled === false &&
      selectedOrg.providerName?.toLowerCase() ===
        Provider.QUICKBOOKS.toLowerCase()
    ) {
      dispatch(
        turnMessageOn({
          title: t('organisation.tax-rates-module-disabled-title'),
          description: t('organisation.tax-rates-module-disabled-description', {
            providerName: selectedOrg.providerName,
          }),

          type: MessageStates.WARNING,
        })
      );
      return false; // Prevent tab change
    }
    return true; // Allow tab change
  };

  const uploadFileInputRef = React.useRef<HTMLInputElement>(null);

  const openUploadTransactions = () => {
    uploadFileInputRef.current?.click();
  };

  const handleUploadFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    if (!isValidExcelOrCsvFile(file)) {
      dispatch(
        turnMessageOn({
          title: t('organisation.wrong-file-type-title'),
          description: t('organisation.wrong-file-type-description'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }

    try {
      const columnNames = await extractColumnNamesFromFile(file);
      dispatch(
        setUploadTransactionsTemp(file, UploadTransactionsSelection.BLANK)
      );
      dispatch(
        turnModalOn({
          title: t('organisation.map-import-fields-title'),
          type: WindowType.EXCEL_TRANSACTIONS,
          maskClosable: false,
          component: (
            <UploadTransactionsColumnsModal
              columnNames={columnNames}
              fileName={file.name}
              organisationId={selectedId}
            />
          ),
        })
      );
    } catch {
      dispatch(
        turnMessageOn({
          title: t('organisation.upload-failed-title'),
          description: t('organisation.upload-failed-description'),
          type: MessageStates.WARNING,
        })
      );
    }
  };

  // Revert: open drawer instead of direct file picker
  // const openUploadTransactionsDrawer = () => {
  //   const drawerData: DrawerData = {
  //     title: 'Upload Transactions',
  //     type: DrawerType.UPLOAD_TRANSACTIONS,
  //     component: <UploadTransactionsDrawer organisationId={selectedId} />,
  //   };
  //   dispatch(turnDrawerOn(drawerData));
  // };

  return (
    <>
      <input
        ref={uploadFileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleUploadFileSelected}
        style={{ display: 'none' }}
        aria-hidden
      />
      <Helmet>
        <title>{`${
          selectedOrg?.friendlyName
            ? selectedOrg?.friendlyName
            : t('organisation.entities')
        } | ${process.env.PROJECT_NAME}`}</title>
        <meta
          name="description"
          content={t('organisation.entities-page-description')}
        />
      </Helmet>

      <OrganisationDetails
        loading={loading}
        organisation={selectedOrg}
        organisationId={selectedId}
        selectedMaps={selectedMaps}
        vatReturnLogs={checkVatReturn()}
        //disconnectOrganisation={disconnectOrganisation}
        countries={countries}
        openEditFriendlyName={openEditFriendlyName}
        addReturn={addReturn}
        editReturnMapping={editReturnMapping}
        vatGenerate={vatGenerate}
        connectOrganisation={onAddEntity}
        currentUser={currentUser}
        loadCustomersAndSuppliers={loadCustomersAndSuppliers}
        activateVatChecker={activateVatChecker}
        handlePreferences={handlePreferences}
        menuSize={menuSize}
        hasAdvancedUserRights={hasAdvancedUserRights}
        downloadAttachment={downloadAttachment}
        checkTaxRatesAccess={checkTaxRatesAccess}
        openReconciliationDrawer={openReconciliationDrawer}
        openUploadTransactions={openUploadTransactions}
      />
    </>
  );
};
